/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import {
	ButtonItem,
	CheckboxItem,
	ErrorTextItem,
	Form as FormType,
	FormItem,
	HeaderItem,
	InputItem,
	LinkItem,
	PasswordItem,
	SelectItem,
	TableCellItem,
	TableItem,
	TabsItem,
	TextItem,
	UploadItem
} from '@cedalo/gateway';
import IconAdd from '@mui/icons-material/esm/Add';
import CloseIcon from '@mui/icons-material/esm/Clear';
import IconEdit from '@mui/icons-material/esm/Edit';
import InfoIcon from '@mui/icons-material/esm/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/esm/Visibility';
import VisibilityOffIcon from '@mui/icons-material/esm/VisibilityOff';
import {
	Box,
	Checkbox,
	FormControlLabel,
	IconButton,
	Input,
	InputAdornment,
	InputBase,
	MenuItem,
	Select,
	Tab,
	Tabs,
	Tooltip,
	Typography
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { withStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import { useLocale } from '../../languages/LocaleProvider';
import theme from '../../theme';
import { useLocalState } from '../app/GraphQLWSClient';
import { setHash, useHashProperty } from './URLHash';

const FormContext = React.createContext<{
	createCustomElement?: (...args) => any;
	formType: string;
	formActionCreator: FormActionCreator;
}>({
	createCustomElement: undefined,
	formType: undefined,
	formActionCreator: undefined
});

export const WIDTH = {
	small: '12rem',
	medium: '22rem',
	large: '32rem'
};

const styles = () => ({
	tableText: {
		paddingLeft: '6px',
		backgroundColor: theme.table.backgroundCell
	},
	tableSelect: {
		color: theme.palette.text.primary,
		background: theme.table.backgroundCell,
		'&:before': {
			// normal
			border: 'none !important'
		},
		'&:after': {
			// focused
			border: 'none'
		},
		'&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error):before': {
			// hover
			border: 'none'
		}
	},
	tableInput: {
		outline: 'none',
		color: theme.palette.text.primary,
		backgroundColor: theme.table.backgroundCell,
		border: '2px solid transparent',
		paddingLeft: '4px',
		'&:focus': {
			borderColor: 'black',
			borderRadius: '4px'
		}
	},
	tableButton: {
		backgroundColor: theme.table.backgroundCell,
		border: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 0,
		color: theme.palette.text.primary,
		'&:active': {
			backgroundColor: `${theme.table.backgroundCellActive} !important`
		},
		'&:hover': {
			backgroundColor: theme.table.backgroundCellHover
		}
	}
});

export type FormAction = (type: 'click' | 'change', id: string, data?: any) => Promise<any>;
export type FormActionCreator = (form: string) => FormAction;
const applyStyles = withStyles(styles);

const noop = () => {};
function assertUnreachable(item: never): never;
function assertUnreachable(item: FormItem | TableCellItem): never {
	throw new Error(`unknown item type ${item.type}`);
}

const ButtonContent = ({ icon }) => {
	const props = {
		style: {
			width: '0.85em',
			height: '0.85em'
		}
	};
	switch (icon) {
		case 'delete':
			return <CloseIcon {...props} />;
		case 'add':
			return <IconAdd {...props} />;
		case 'edit':
			return <IconEdit {...props} />;
		default:
			return icon;
	}
};

const TableButton = applyStyles(
	({
		location,
		classes,
		formAction,
		icon,
		actionId,
		actionData,
		tooltip
	}: ButtonItem & {
		formAction: FormAction;
		classes: any;
	}) => (
		<Tooltip enterDelay={300} title={tooltip || ''}>
			<button
				onClick={() => formAction('click', actionId, actionData)}
				style={{ gridArea: location, cursor: 'pointer' }}
				className={classes.tableButton}
				type="button"
			>
				<ButtonContent icon={icon} />
			</button>
		</Tooltip>
	)
);
const TableLink = applyStyles(
	({ location, classes, icon, href, tooltip }: LinkItem & { formAction: FormAction; classes: any }) => (
		<Tooltip enterDelay={300} title={tooltip || ''}>
			<button
				onClick={() => setHash(href)}
				style={{ gridArea: location, cursor: 'pointer' }}
				className={classes.tableButton}
				type="button"
			>
				<ButtonContent icon={icon} />
			</button>
		</Tooltip>
	)
);

const TableHeader = applyStyles(({ location, value, href, style }: HeaderItem & {}) => {
	const { locale } = useLocale();
	return (
		<div
			style={{
				display: 'flex',
				color: theme.palette.text.primary,
				backgroundColor: theme.table.backgroundHeader,
				alignItems: 'center',
				justifyContent: 'center',
				gridArea: location,
				cursor: href ? 'pointer' : undefined,
				...style
			}}
			onClick={href ? () => setHash(href) : undefined}
		>
			<Typography pl="1" fontWeight="500">
				{value[locale] || value}
			</Typography>
		</div>
	);
});

const TableInput = applyStyles(
	({
		formAction,
		path,
		value: remoteValue,
		location,
		classes,
		adornment
	}: InputItem & {
		classes: any;
		formAction: FormAction;
	}) => {
		const [value, setLocal, , isChanged] = useLocalState(remoteValue);
		return (
			<InputBase
				style={{ gridArea: location }}
				className={classes.tableInput}
				onKeyUp={async (e) => {
					if (e.key === 'Escape') {
						setLocal(null);
						setImmediate(() => e.target.blur());
					} else if (e.key === 'Enter') {
						try {
							if (isChanged) {
								await formAction('change', path, value);
							}
						} catch (error) {
							console.error(error);
						}
						e.target.blur();
					}
				}}
				value={value}
				onChange={(event) => {
					setLocal(event.target.value);
				}}
				onBlur={async () => {
					try {
						if (isChanged) {
							await formAction('change', path, value);
						}
					} catch (error) {
						console.error(error);
					}
				}}
				startAdornment={
					adornment && adornment.position === 'start' ? (
						<InputAdornment position="start">{adornment.value}</InputAdornment>
					) : null
				}
				endAdornment={
					adornment && adornment.position === 'end' ? (
						<InputAdornment position="end">{adornment.value}</InputAdornment>
					) : null
				}
			/>
		);
	}
);

const TableSelect = applyStyles(
	({
		formAction,
		path,
		value,
		options,
		location,
		classes
	}: SelectItem & {
		formAction: FormAction;
		classes: any;
	}) => {
		const { locale } = useLocale();
		return (
			<Select
				style={{
					gridArea: location,
					paddingLeft: '4px'
				}}
				// id="font-name"
				value={value}
				// value={tf.fontname || ''}
				onChange={(event) => formAction('change', path, event.target.value)}
				input={<Input name={path} id={path} />}
				className={classes.tableSelect}
				inputProps={{
					style: { paddingLeft: '4px', paddingTop: '6px' }
				}}
			>
				{options.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label[locale] || option.label}
					</MenuItem>
				))}
			</Select>
		);
	}
);

const TableText = applyStyles(({ value, location, classes }: TextItem & { classes: any }) => {
	return (
		<Tooltip enterDelay={300} title={value}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gridArea: location,
					color: theme.palette.text.primary
				}}
				className={classes.tableText}
			>
				<Typography
					fontStyle="italic"
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap'
					}}
				>
					{value}
				</Typography>
			</Box>
		</Tooltip>
	);
});

const TableErrorText = applyStyles(({ value, location }: ErrorTextItem) => {
	return (
		<Tooltip enterDelay={300} title={value}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					paddingLeft: '4px',
					backgroundColor: 'red',
					color: 'white',
					gridArea: location
				}}
			>
				<Typography
					fontWeight={500}
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap'
					}}
				>
					{value}
				</Typography>
			</Box>
		</Tooltip>
	);
});

const FormSelect = applyStyles(
	({
		formAction,
		path,
		value: remoteValue,
		options,
		location,
		width = 'medium',
		label,
		tooltip,
		error
	}: SelectItem & {
		formAction: FormAction;
		classes: any;
	}) => {
		const [value, setLocal] = useLocalState(remoteValue);
		const { locale } = useLocale();
		return (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gridArea: location
				}}
			>
				<TextField
					style={{
						maxWidth: WIDTH[width],
						flex: '1'
					}}
					select
					variant="outlined"
					size="small"
					// margin="normal"
					// id="font-name"
					value={value}
					// value={tf.fontname || ''}
					onChange={(event) => {
						setLocal(event.target.value);
						formAction('change', path, event.target.value);
					}}
					label={label ? label[locale] || label : undefined}
					error={!!error}
					helperText={error}
					// input={<Input name={path} id={path} />}
					// className={classes.tableSelect}
					// inputProps={{
					// 	style: { paddingLeft: '4px', paddingTop: '6px' }
					// }}
				>
					{options.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label[locale] || option.label}
						</MenuItem>
					))}
				</TextField>
				{tooltip ? (
					<Tooltip enterDelay={300} title={tooltip[locale] || tooltip}>
						<InfoIcon
							style={{
								fill: '#888888',
								fontSize: '1rem',
								paddingLeft: '1rem'
							}}
						/>
					</Tooltip>
				) : null}
			</div>
		);
	}
);

const FormHeader = applyStyles(
	({
		value,
		location
	}: HeaderItem & {
		classes: any;
	}) => {
		const { locale } = useLocale();
		return (
			<Typography
				variant="h6"
				style={{ gridArea: location, paddingLeft: '16px', color: theme.palette.text.primary }}
			>
				{value[locale] || value}
			</Typography>
		);
	}
);

const FormCheckbox = ({
	path,
	label,
	value: remoteValue,
	tooltip,
	formAction
}: CheckboxItem & { formAction: FormAction }) => {
	const [value, setLocal] = useLocalState(remoteValue);
	const { locale } = useLocale();

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center'
			}}
		>
			<FormControlLabel
				style={{
					justifySelf: 'self-start'
				}}
				control={
					<Checkbox
						checked={value}
						onChange={(event, newValue) => {
							setLocal(newValue);
							formAction('change', path, newValue);
						}}
					/>
				}
				label={label[locale] || label}
			/>
			{tooltip ? (
				<Tooltip enterDelay={300} title={tooltip[locale] || tooltip}>
					<InfoIcon
						style={{
							fill: '#888888',
							fontSize: '1rem'
						}}
					/>
				</Tooltip>
			) : null}
		</div>
	);
};

const FormInput = ({
	formAction,
	path,
	value: remoteValue,
	location,
	label,
	width = 'medium',
	tooltip
}: InputItem & { formAction: FormAction }) => {
	const [value, setLocal, , isChanged] = useLocalState(remoteValue);
	const { locale } = useLocale();

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gridArea: location
			}}
		>
			<TextField
				style={{
					maxWidth: WIDTH[width],
					flex: '1'
				}}
				variant="outlined"
				size="small"
				// margin="normal"
				onKeyUp={async (e) => {
					if (e.key === 'Escape') {
						setLocal(null);
						setImmediate(() => e.target.blur());
					} else if (e.key === 'Enter') {
						try {
							if (isChanged) {
								await formAction('change', path, value);
							}
						} catch (error) {
							console.error(error);
						}
						e.target.blur();
					}
				}}
				label={label[locale] || label}
				value={value}
				onChange={(event) => {
					setLocal(event.target.value);
				}}
				onBlur={async () => {
					try {
						if (isChanged) {
							await formAction('change', path, value);
						}
					} catch (error) {
						console.error(error);
					}
				}}
			/>
			{tooltip ? (
				<Tooltip enterDelay={300} title={tooltip[locale] || tooltip}>
					<InfoIcon
						style={{
							fill: '#888888',
							fontSize: '1rem',
							paddingLeft: '1rem'
						}}
					/>
				</Tooltip>
			) : null}
		</div>
	);
};

const FormPassword = ({
	formAction,
	path,
	value: remoteValue,
	location,
	label,
	width = 'medium',
	tooltip
}: PasswordItem & { formAction: FormAction }) => {
	const [value, setLocal, , isChanged] = useLocalState(remoteValue);
	const [showPassword, setShowPassword] = useState(false);
	const { locale } = useLocale();
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gridArea: location
			}}
		>
			<TextField
				style={{
					maxWidth: WIDTH[width],
					flex: '1'
				}}
				type={showPassword ? undefined : 'password'}
				variant="outlined"
				size="small"
				// margin="normal"
				onKeyUp={async (e) => {
					if (e.key === 'Escape') {
						setLocal(null);
						setImmediate(() => e.target.blur());
					} else if (e.key === 'Enter') {
						try {
							if (isChanged) {
								await formAction('change', path, value);
							}
						} catch (error) {
							console.error(error);
						}
						e.target.blur();
					}
				}}
				label={label[locale] || label}
				value={value}
				onChange={(event) => {
					setLocal(event.target.value);
				}}
				onBlur={async () => {
					try {
						if (isChanged) {
							await formAction('change', path, value);
						}
					} catch (error) {
						console.error(error);
					}
				}}
				InputProps={{
					autoComplete: `new-password`,
					style: {
						paddingRight: '4px'
					},
					endAdornment: (
						<InputAdornment position="end">
							<IconButton size="small" onClick={() => setShowPassword((current) => !current)}>
								{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
							</IconButton>
						</InputAdornment>
					)
				}}
			/>
			{tooltip ? (
				<Tooltip enterDelay={300} title={tooltip[locale] || tooltip}>
					<InfoIcon
						style={{
							fill: '#888888',
							fontSize: '1rem',
							paddingLeft: '1rem'
						}}
					/>
				</Tooltip>
			) : null}
		</div>
	);
};

const FormUpload = ({
	formAction,
	path,
	value: remoteValue,
	location,
	label,
	error,
	width = 'medium',
	tooltip
}: UploadItem & { formAction: FormAction }) => {
	const [value, setLocal, , isChanged] = useLocalState(remoteValue);
	const { locale } = useLocale();
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gridArea: location
			}}
		>
			{value ? (
				<TextField
					style={{
						maxWidth: WIDTH[width],
						flex: '1'
					}}
					variant="outlined"
					size="small"
					disabled
					label={label[locale] || label}
					value={value}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									size="small"
									onClick={async () => {
										try {
											await formAction('change', path, undefined);
										} catch (error) {
											console.error(error);
										}
									}}
								>
									<CloseIcon />
								</IconButton>
							</InputAdornment>
						)
					}}
				/>
			) : (
				<TextField
					style={{
						maxWidth: WIDTH[width],
						flex: '1'
					}}
					variant="outlined"
					size="small"
					inputProps={{
						accept: '*',
						type: 'file'
					}}
					InputLabelProps={{
						shrink: true
					}}
					error={!!error}
					helperText={error}
					label={label[locale] || label}
					onChange={(event) => {
						// @ts-ignore
						const { files, value } = event.target;
						if (files && files.length) {
							const reader = new FileReader();
							reader.onloadend = async (fe) => {
								try {
									await formAction('change', path, { name: files[0].name, data: fe.target.result });
									// setLocal(null);
								} catch (error) {
									console.error(error);
								}
							};
							reader.readAsText(files[0]);
							setLocal(files[0].name);
						}
					}}
				/>
			)}
			{tooltip ? (
				<Tooltip enterDelay={300} title={tooltip[locale] || tooltip}>
					<InfoIcon
						style={{
							fill: '#888888',
							fontSize: '1rem',
							paddingLeft: '1rem'
						}}
					/>
				</Tooltip>
			) : null}
		</div>
	);
};

export const FormTabs = ({ items, location, formAction }: TabsItem & { formAction: FormAction }) => {
	const [currentTab, setCurrentTab] = useHashProperty('tab', null, { clear: true });
	const { locale } = useLocale();
	const setCurrentTabByIndex = (index) => setCurrentTab(items[index].key || index);
	let currentTabIndex = items.findIndex((i) => i.key === currentTab);
	currentTabIndex = currentTabIndex === -1 ? Number(currentTab) : currentTabIndex;
	const { createCustomElement, formActionCreator } = useContext(FormContext);

	const validIndex = currentTab && currentTabIndex && currentTabIndex < items.length;
	useEffect(() => {
		if (!validIndex) {
			setCurrentTabByIndex(0);
		}
	}, [validIndex]);

	return (
		<div
			style={{
				gridArea: location
			}}
		>
			<Tabs value={currentTabIndex} onChange={(event, value) => setCurrentTabByIndex(value)} variant="fullWidth">
				{items.map(({ label, key }) => (
					<Tab key={key} label={label[locale] || label} />
				))}
			</Tabs>
			{items.map((formProps, index) => {
				const isCurrent = currentTab === formProps.key || currentTabIndex === index;
				return (
					<Box
						key={formProps.key}
						sx={{ margin: '16px 0px 8px' }}
						style={{
							display: 'grid',
							// alignItems: 'center',
							gridTemplateColumns: formProps.layout,
							// gridAutoRows: '32px',
							rowGap: '16px'
						}}
						hidden={!isCurrent}
					>
						{isCurrent
							? formProps.items.map((item) => {
									switch (item.type) {
										case 'form': {
											return (
												<Form
													subForm
													key={item.key}
													formActionCreator={formActionCreator}
													{...item}
													formAction={formAction}
												/>
											);
										}
										case 'table': {
											return <Table key={item.key} {...item} formAction={formAction} />;
										}
										case 'input': {
											return <FormInput key={item.key} {...item} formAction={formAction} />;
										}
										case 'password': {
											return <FormPassword key={item.key} {...item} formAction={formAction} />;
										}
										case 'upload': {
											return <FormUpload key={item.key} {...item} formAction={formAction} />;
										}
										case 'checkbox': {
											// return item.type;
											return <FormCheckbox key={item.key} {...item} formAction={formAction} />;
										}
										case 'select': {
											return <FormSelect key={item.key} {...item} formAction={formAction} />;
										}
										case 'header': {
											return <FormHeader key={item.key} {...item} />;
										}
										case 'text': {
											return item.type;
											// return <FormText key={item.key} {...item} formAction={formAction}/>
										}
										case 'button': {
											return item.type;
											// return <FormText key={item.key} {...item} formAction={formAction}/>
										}
										case 'tabs': {
											return <FormTabs key={item.key} {...item} formAction={formAction} />;
										}
										case 'custom':
											return createCustomElement(item);
										default:
											assertUnreachable(item);
									}
							  })
							: null}
					</Box>
				);
			})}
		</div>
	);
};
export const Table = ({ layout, items, location, formAction }: TableItem & { formAction: FormAction }) => {
	return (
		<div
			style={{
				display: 'grid',
				gridArea: location,
				// alignItems: 'center',
				gridTemplateColumns: layout,
				gridAutoRows: '32px',
				backgroundColor: '#dcdcdc',
				borderRadius: '4px',
				border: '1px solid #dcdcdc',
				overflow: 'hidden',
				gap: '1px'
			}}
		>
			{items.map((item) => {
				switch (item.type) {
					case 'header': {
						return <TableHeader key={item.key} {...item} />;
					}
					case 'input': {
						return <TableInput key={item.key} {...item} formAction={formAction} />;
					}
					case 'button': {
						return <TableButton key={item.key} {...item} formAction={formAction} />;
					}
					case 'select': {
						return <TableSelect key={item.key} {...item} formAction={formAction} />;
					}
					case 'link': {
						return <TableLink key={item.key} {...item} formAction={formAction} />;
					}
					case 'text': {
						return <TableText key={item.key} {...item} />;
					}
					case 'error': {
						return <TableErrorText key={item.key} {...item} />;
					}
					default:
						assertUnreachable(item);
				}
			})}
		</div>
	);
};

export const Form = ({
	layout,
	items,
	style = {},
	subForm = false,
	...props
}: FormType & {
	formAction?: FormAction;
	formActionCreator: FormActionCreator;
	style?: React.CSSProperties;
	createCustomElement?: any;
	subForm: boolean;
}) => {
	const context = useContext(FormContext);
	const formType = props.formType || context.formType;
	const createCustomElement = props.createCustomElement || context.createCustomElement;
	const formActionCreator = props.formActionCreator || context.formActionCreator;
	const formAction = formActionCreator(formType);
	return (
		<FormContext.Provider value={{ createCustomElement, formType, formActionCreator }}>
			<Box
				component={subForm ? 'div' : 'form'}
				autoComplete="off"
				style={{
					display: 'grid',
					// alignItems: 'center',
					gridTemplateColumns: layout,
					// gridAutoRows: '32px',
					rowGap: '16px',
					...style
				}}
			>
				{items.map((item) => {
					switch (item.type) {
						case 'form': {
							return <Form formActionCreator={formActionCreator} subForm key={item.key} {...item} formAction={formAction} />;
						}
						case 'table': {
							return <Table key={item.key} {...item} formAction={formAction} />;
						}
						case 'input': {
							return <FormInput key={item.key} {...item} formAction={formAction} />;
						}
						case 'password': {
							return <FormPassword key={item.key} {...item} formAction={formAction} />;
						}
						case 'upload': {
							return <FormUpload key={item.key} {...item} formAction={formAction} />;
						}
						case 'checkbox': {
							// return item.type;
							return <FormCheckbox key={item.key} {...item} formAction={formAction} />;
						}
						case 'select': {
							return <FormSelect key={item.key} {...item} formAction={formAction} />;
						}
						case 'header': {
							return <FormHeader key={item.key} {...item} />;
						}
						case 'text': {
							return item.type;
							// return <FormText key={item.key} {...item} formAction={formAction}/>
						}
						case 'button': {
							return item.type;
							// return <FormText key={item.key} {...item} formAction={formAction}/>
						}
						case 'tabs': {
							return <FormTabs key={item.key} {...item} formAction={formAction} />;
						}
						case 'custom':
							return createCustomElement(item);
						default:
							assertUnreachable(item);
					}
				})}
			</Box>
		</FormContext.Provider>
	);
};
